import SelectRobotModal from '../../Robot/RobotView/RobotLeftBlock/RobotDetails/RobotMissionMainStatus/SelectRobotModal/SelectRobotModal'
import { RobotTypeEnum } from 'api/schema'
import { RobotRead } from 'api/schema'
import { CleaningZoneStatus, TransferRouteStatus } from 'api/schema/index'
import clsx from 'clsx'
import Box from 'components/Box/Box'
import { Button } from 'components/Button/Button'
import CommonButton from 'components/Button/ButtonTypes/CommonButton'
import RobotTypeLabel from 'components/RobotTypeLabel/RobotTypeLabel'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import Title from 'components/Title/Title'
import { MultiLineString, Polygon } from 'geojson'
import { ReactComponent as GearSvg } from 'images/icons/gear.svg'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { RobotActions } from 'store/actions/robot'
import {
  selectCleaningZones,
  selectMission,
  selectMissionCalculatingStatus,
  selectMissionEditingZone,
  selectRestrictZones,
} from 'store/selectors/mission'
import store from 'store/store'
import { missionThunks } from 'store/thunks/mission'
import { robotThunks } from 'store/thunks/robot'
import {
  MISSION_CALCULATING_STATUSES,
  MISSION_PAGE_MODES,
  MISSION_ZONE_TYPES,
  TIMEOUT_FETCHING_STATUS,
} from 'utils/constants'
import { VENDOR } from 'utils/vendorDisplayName'

import CalculatingLoader from './CalculatingLoader/CalculatingLoader'
import { DrawDropdown } from './MissionButtons/DrawDropdown/DrawDropdown'
import MissionButtons from './MissionButtons/MissionButtons'
import MissionSettingsModal from './MissionButtons/MissionSettingsModal/MissionSettingsModal'
import MissionMap from './MissionMap/MissionMap'
import DrawZoneMode from './Modes/DrawZone/DrawZoneMode'
import ViewZonesMode from './Modes/ViewZone/ViewZonesMode'

import style from './MissionView.module.css'
import pageLayoutStyle from 'styles/pageLayout.module.css'

const MissionView = () => {
  const mission = useSelector(selectMission)
  const cleaningZones = useSelector(selectCleaningZones)
  const restrictZones = useSelector(selectRestrictZones)
  const calculatingStatus = useSelector(selectMissionCalculatingStatus)
  const editingZone = useSelector(selectMissionEditingZone)
  const dispatch = useDispatch()

  const [ready, setReady] = useState<[number, number, number, number]>([0, 0, 0, 0]) // OverallCleaning, ReadyCleaning, OverallTransfer, ReadyTransfer
  const [calculationMode, setCalculationMode] = useState<'all' | 'single' | undefined>(undefined)
  const [calculationIsActive, setCalculationIsActive] = useState<boolean>(false)
  const [hideCleaningType, setHideCleaningType] = useState<boolean>(
    !mission.missionInfo || !mission.missionInfo.robot_type || mission.missionInfo.robot_type !== RobotTypeEnum.COMBO
  )
  const { t } = useTranslation()
  const [selectedRobot, setSelectedRobot] = useState<RobotRead | undefined>(undefined)
  const [showRobotModal, setShowRobotModal] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  useEffect(() => {
    // When the component mounts or unmounts, clear the robot from the Redux store
    // to prevent its position from being shown if navigated from MissionControl.
    dispatch(RobotActions.setRobotInfo(undefined))
    return () => {
      dispatch(RobotActions.setRobotInfo(undefined))
    }
  }, [dispatch])

  useEffect(() => {
    const fetchData = async () => {
      if (mission.missionInfo) {
        try {
          const response = await store.dispatch(missionThunks.fetchCalculationStatus(mission.missionInfo?.id))
          if (!response) return

          const overallCleaning = response.cleaning_zones_statuses?.length ?? 0
          const overallTransfers = overallCleaning > 0 ? overallCleaning - 1 : 1
          let readyCleaning = 0
          let readyTransfer = 0

          // An additional logic to control calculationMode state.
          // It needs for the correct CalculatingLoader view displaying
          if (
            !calculationIsActive &&
            response.status === MISSION_CALCULATING_STATUSES.CALCULATING &&
            calculationMode !== undefined
          ) {
            setCalculationIsActive(true)
          }

          // Get readyCleaning count
          if (response.cleaning_zones_statuses.length > 0) {
            const cleaningReadyCount: number = response.cleaning_zones_statuses.filter(
              (zone: CleaningZoneStatus) => zone.status === 'ok'
            ).length
            readyCleaning += cleaningReadyCount
          }
          // Get readyTransfer count
          if (response.transfer_routes_statuses.length > 0) {
            const transferReadyCount: number = response.transfer_routes_statuses.filter(
              (zone: TransferRouteStatus) => zone.status === 'ok'
            ).length
            readyTransfer += transferReadyCount
          }
          setReady([overallCleaning, readyCleaning, overallTransfers, readyTransfer])

          // Update page case
          if (response.status === MISSION_CALCULATING_STATUSES.CALCULATING && calculationMode === undefined) {
            if (
              response.cleaning_zones_statuses.filter((zone: CleaningZoneStatus) => zone.status === 'calculating')
                .length > 1
            ) {
              setCalculationMode('all')
            } else {
              setCalculationMode('single')
            }
          }

          // Reset CalculationMode
          if (calculationIsActive && response.status !== MISSION_CALCULATING_STATUSES.CALCULATING) {
            setCalculationIsActive(false)
            setCalculationMode(undefined)
          }
        } catch (error) {
          console.log('Unable to get mission status')
        }
      }
    }

    fetchData() // Fetch data initially

    const interval = setInterval(fetchData, TIMEOUT_FETCHING_STATUS)

    return () => clearInterval(interval)
    // missionId — avoid restarting interval on every mission update (setZonesStatus gives new ref and caused request loop)
  }, [mission.missionInfo?.id, calculatingStatus, calculationIsActive, calculationMode])

  const onSelectRobotClick = () => {
    if (selectedRobot) {
      setSelectedRobot(undefined)
      dispatch(RobotActions.setRobotInfo(undefined))
      return
    }
    setShowRobotModal(true)
  }
  const onRobotSelected = async (robot: RobotRead | undefined) => {
    setShowRobotModal(false)
    setSelectedRobot(robot)
    if (robot) {
      store.dispatch(robotThunks.fetchRobot(robot.id))
    }
  }

  useEffect(() => {
    setHideCleaningType(
      !mission.missionInfo || !mission.missionInfo.robot_type || mission.missionInfo.robot_type !== RobotTypeEnum.COMBO
    )
  }, [mission.missionInfo])

  const onSaveZoneClicked = async (
    zoneName: string,
    zoneDescription: string,
    zoneType: MISSION_ZONE_TYPES,
    cleaningSpeed?: number,
    transportSpeed?: number,
    isBorderCleaning?: boolean,
    equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean }
  ) => {
    if (!mission.tempZone) {
      return
    }
    if (zoneType === MISSION_ZONE_TYPES.CLEANING || zoneType === MISSION_ZONE_TYPES.RECORDED) {
      await store.dispatch(
        missionThunks.createCleaningZone(
          zoneName,
          zoneDescription,
          mission.tempZone.geometry as Polygon,
          mission.tempZone.properties.route_id,
          mission.tempLaunchPoint?.geometry,
          cleaningSpeed,
          transportSpeed,
          isBorderCleaning,
          mission.tempZoneRoute?.geometry as MultiLineString,
          equipment_setup,
          zoneType === MISSION_ZONE_TYPES.RECORDED
        )
      )
    } else if (zoneType === MISSION_ZONE_TYPES.RESTRICT) {
      store.dispatch(missionThunks.createRestrictZone(zoneName, zoneDescription, mission.tempZone.geometry as Polygon))
    }
    if (mission.tempZoneRoute) {
      await store.dispatch(missionThunks.fetchMissionBackground(mission.missionInfo?.id || -1))
    }
  }

  const onSaveChangedZoneClicked = async (
    zoneName: string,
    zoneDescription: string,
    zoneType: MISSION_ZONE_TYPES,
    cleaningSpeed?: number,
    transportSpeed?: number,
    isBorderCleaning?: boolean,
    equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean }
  ) => {
    if (!mission.tempZone) {
      return
    }
    if (zoneType === MISSION_ZONE_TYPES.CLEANING || zoneType === MISSION_ZONE_TYPES.RECORDED) {
      await store.dispatch(
        missionThunks.editCleaningZone(
          mission.tempZone.properties?.zone_id,
          zoneName,
          zoneDescription,
          mission.tempZone.geometry as Polygon,
          mission.tempZoneRoute?.geometry as MultiLineString,
          mission.tempZone.properties.route_id,
          mission.tempLaunchPoint?.geometry,
          cleaningSpeed,
          transportSpeed,
          isBorderCleaning,
          equipment_setup
        )
      )
    } else {
      store.dispatch(
        missionThunks.editRestrictZone(
          mission.tempZone.properties?.zone_id,
          zoneName,
          zoneDescription,
          mission.tempZone.geometry as Polygon
        )
      )
    }
  }
  const onSaveChangedZoneRouteClicked = async (
    zoneName: string,
    zoneDescription: string,
    zoneType: MISSION_ZONE_TYPES,
    cleaningSpeed?: number,
    transportSpeed?: number,
    isBorderCleaning?: boolean,
    equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean }
  ) => {
    if (!mission.tempZoneRoute || !editingZone || !mission.tempZone) {
      console.log('No temp route or zone')
      return
    }
    await store.dispatch(
      missionThunks.editCleaningZone(
        editingZone.properties?.zone_id,
        zoneName,
        zoneDescription,
        mission.tempZone?.geometry as Polygon,
        mission.tempZoneRoute.geometry as MultiLineString,
        mission.tempZone.properties.route_id,
        mission.tempLaunchPoint?.geometry,
        cleaningSpeed,
        transportSpeed,
        isBorderCleaning,
        equipment_setup
      )
    )
    await store.dispatch(missionThunks.fetchMissionBackground(mission.missionInfo?.id || -1))
  }

  const modeList = {
    [`${MISSION_PAGE_MODES.DRAW_ZONE}`]: <DrawZoneMode onSaveClicked={onSaveZoneClicked} />,
    [`${MISSION_PAGE_MODES.VIEW_ZONES}`]: (
      <ViewZonesMode
        cleaningZones={cleaningZones}
        restrictZones={restrictZones}
        setCalculationMode={setCalculationMode}
        hideCleaningType={hideCleaningType}
      />
    ),
    [`${MISSION_PAGE_MODES.DRAW_ROUTE}`]: <DrawZoneMode onSaveClicked={onSaveZoneClicked} />,
    [`${MISSION_PAGE_MODES.EDIT_ZONE}`]: <DrawZoneMode onSaveClicked={onSaveChangedZoneClicked} zone={editingZone} />,
    [`${MISSION_PAGE_MODES.EDIT_ZONE_ROUTE}`]: (
      <DrawZoneMode onSaveClicked={onSaveChangedZoneRouteClicked} zone={editingZone} />
    ),
  }

  return (
    <>
      <CalculatingLoader
        visible={calculatingStatus === MISSION_CALCULATING_STATUSES.CALCULATING && calculationMode !== undefined}
        ready={ready}
        mode={calculationMode}
      />
      <>
        <div className={pageLayoutStyle.pageHeaderArea}>
          <div className={clsx(style.titleBlock, pageLayoutStyle.titleBlockMargin)}>
            <div className={style.titleRow}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Title text={mission.missionInfo?.name || ''} />
                <div className={clsx('text-16-300', style.titleId)}>
                  {t('missions.id')}: {mission.missionInfo?.id}
                </div>
              </div>
              <div className={style.selectBlock}>
                <DrawDropdown className={style.headerDropdown} />
                <CommonButton className={style.headerButton} onClick={() => setIsSettingsOpen(true)}>
                  <GearSvg />
                </CommonButton>
                <MissionSettingsModal
                  isOpen={isSettingsOpen}
                  onClose={() => setIsSettingsOpen(false)}
                  missionId={mission.missionInfo?.id || -1}
                  missionName={mission.missionInfo?.name || ''}
                  locationName={mission.missionInfo?.location}
                />
                <SelectRobotModal
                  isOpen={showRobotModal}
                  robotType={undefined}
                  showOnline={true}
                  onClose={() => setShowRobotModal(false)}
                  onConfirmClick={onRobotSelected}
                />
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'flex-start' }}>
            <RobotTypeLabel vendor={VENDOR.AUTONOMICS} robotType={mission.missionInfo?.robot_type} />
          </div>
        </div>
        <div className={style.main}>
          <div className={style.leftBlock}>
            {/* MissionButtons removed */}
            <Box className={style.box}>{modeList[mission.mode]}</Box>
          </div>
          <div className={style.map}>
            <MissionMap selectedRobot={selectedRobot || undefined} onSelectRobotClick={onSelectRobotClick} />
          </div>
        </div>
      </>
    </>
  )
}

export default MissionView
